import type { RightDrawerTab } from "@src/components/agent-chat/right-drawer-tab-store";
import type {
	CardDocumentId,
	PreprintDocumentId,
} from "@src/components/collaboration/document-id";
import type { SupportedAgent, SupportedModel } from "@src/gql/graphql";
import type { RightDrawerTrigger } from "@src/utils/analytics";
import type { ReactNode } from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from "react";
import type { TLShapeId } from "tldraw";
import type { GenerationState } from "../cards/preprint-card/context/preprint-card-meta-context";

interface OpenCardDrawerEvent {
	type: "open-card-drawer";
	payload: {
		shapeId: TLShapeId;
		trigger: RightDrawerTrigger;
	};
}

interface CloseCardDrawerEvent {
	type: "close-card-drawer";
	payload: {
		shapeId: TLShapeId;
	};
}

interface ToggleCardDrawerEvent {
	type: "toggle-card-drawer";
	payload: {
		shapeId: TLShapeId;
		trigger: RightDrawerTrigger;
	};
}

interface FitToContentEvent {
	type: "fit-to-content";
	payload: {
		shapeId: TLShapeId;
	};
}

interface SetAutoResizeEvent {
	type: "set-auto-resize";
	payload: {
		shapeId: TLShapeId;
		autoResize: boolean;
	};
}

interface EnableCardEvent {
	type: "enable-card";
	payload: {
		shapeId: TLShapeId;
	};
}

interface DisableCardEvent {
	type: "disable-card";
	payload?: {
		shapeIdToExclude: TLShapeId;
	};
}

interface MailCardContentEvent {
	type: "mail-card-content";
	payload: {
		cardId: string;
		html: string;
		userEmail: string;
	};
}

interface SetColorEvent {
	type: "set-color";
	payload: {
		shapeId: string;
		color: string;
	};
}

interface CopyLinkToCardEvent {
	type: "copy-link-to-card";
	payload: {
		shapeId: string;
	};
}

interface CreateSyncedCopyEvent {
	type: "create-synced-copy";
	payload: {
		shapeId: string;
	};
}

interface DownloadCardEvent {
	type: "download-card";
	payload: {
		cardId: string;
		type: "docx" | "pdf" | "markdown";
	};
}

interface NewGenerationEvent {
	type: "new-generation";
	payload: {
		preprintCardId: PreprintDocumentId;
		promptCardId: CardDocumentId;
		model: SupportedModel;
		agent: SupportedAgent;
		contentCardIds: (CardDocumentId | PreprintDocumentId)[];
		promptText?: string;
		webSearch?: boolean;
	};
}

interface SetModelAvailabilityEvent {
	type: "set-model-availability";
	payload: {
		generationState: GenerationState;
	};
}

interface CreatePreprintVersionEvent {
	type: "create-preprint-version";
	payload: {
		promptText: string;
		promptCardId: CardDocumentId;
		model: SupportedModel;
		agent: SupportedAgent;
		contentCardIds: (CardDocumentId | PreprintDocumentId)[];
		preprintCardId: PreprintDocumentId;
		webSearch?: boolean;
	};
}

interface PreprintCardDeleteEvent {
	type: "preprint-card-delete";
	payload: {
		shapeId: string;
	};
}

interface SetTextCursorPositionEvent {
	type: "set-text-cursor-position";
	payload: {
		cardId: string;
		query?: string;
	};
}

interface CardQuickActionEvent {
	type: "card-quick-action";
	payload: {
		shapeId: string;
		allModels?: boolean;
	};
}

interface CopyDraftToNoteEvent {
	type: "copy-draft-to-note";
	payload: {
		sourceCardId: string;
	};
}

interface CardSectionDeleteEvent {
	type: "card-section-delete";
	payload: {
		shapeId: TLShapeId;
	};
}

interface AskIllumiActionEvent {
	type: "ask-illumi-action";
	payload: {
		cardEntityId: string;
	};
}

interface OpenCardHistoryEvent {
	type: "open-card-history";
	payload: {
		shapeId: TLShapeId;
	};
}

interface RemoteCardShapeChangeEvent {
	type: "remote-card-shape-change";
	payload: {
		shapeId: TLShapeId;
	};
}

interface OpenBoardSearchEvent {
	type: "open-board-search";
	payload?: {
		source?: "shortcut" | "menu";
	};
}

interface CloseAllCardDrawersEvent {
	type: "close-all-card-drawers";
}

interface OpenAgentChatDrawerEvent {
	type: "open-agent-chat-drawer";
	payload?: {
		tab?: RightDrawerTab;
	};
}

interface CloseAgentChatDrawerEvent {
	type: "close-agent-chat-drawer";
}

interface ToggleAgentChatDrawerEvent {
	type: "toggle-agent-chat-drawer";
	payload?: {
		tab?: RightDrawerTab;
	};
}

interface SaveWhiteboardEvent {
	type: "save-whiteboard";
}

interface ExportBoardEvent {
	type: "export-board";
	payload: {
		format: "png" | "pdf";
		scope?: "all" | "viewport" | "selection";
		hideCardChrome?: boolean;
		background?: boolean;
		action?: "download" | "copy";
	};
}

interface OpenExportBoardModalEvent {
	type: "open-export-board-modal";
	payload?: {
		initialScope?: "all" | "viewport" | "selection";
	};
}

interface AiLimitReachedEvent {
	type: "ai-limit-reached";
	payload?: {
		model?: SupportedModel;
	};
}

interface WorkflowPreprintCompletedEvent {
	type: "workflow-preprint-completed";
	payload: {
		preprintShapeId: TLShapeId;
	};
}

interface PlaceCardOnBoardEvent {
	type: "place-card-on-board";
	payload: {
		cardEntityId: string;
	};
}

export type BoardEvent =
	| OpenCardDrawerEvent
	| CloseCardDrawerEvent
	| ToggleCardDrawerEvent
	| FitToContentEvent
	| SetAutoResizeEvent
	| EnableCardEvent
	| DisableCardEvent
	| MailCardContentEvent
	| SetColorEvent
	| CopyLinkToCardEvent
	| CreateSyncedCopyEvent
	| DownloadCardEvent
	| NewGenerationEvent
	| CreatePreprintVersionEvent
	| SetModelAvailabilityEvent
	| PreprintCardDeleteEvent
	| SetTextCursorPositionEvent
	| CardQuickActionEvent
	| CopyDraftToNoteEvent
	| CardSectionDeleteEvent
	| AiLimitReachedEvent
	| AskIllumiActionEvent
	| OpenCardHistoryEvent
	| RemoteCardShapeChangeEvent
	| OpenBoardSearchEvent
	| CloseAllCardDrawersEvent
	| OpenAgentChatDrawerEvent
	| CloseAgentChatDrawerEvent
	| ToggleAgentChatDrawerEvent
	| SaveWhiteboardEvent
	| ExportBoardEvent
	| OpenExportBoardModalEvent
	| WorkflowPreprintCompletedEvent
	| PlaceCardOnBoardEvent;

type BoardEventListener<T extends BoardEvent["type"] = BoardEvent["type"]> = (
	event: Extract<BoardEvent, { type: T }>,
) => void;

export type EmissionMode = "immediate" | "deferred" | "deferred-once";

export interface EmissionOptions {
	mode?: EmissionMode;
	key?: string;
}

export interface SubscriptionOptions {
	mode?: EmissionMode;
	key?: string;
}

interface DeferredEvent {
	mode: EmissionMode;
	event: BoardEvent;
	key?: string;
}

interface DeferredEventsStorage {
	unkeyed: DeferredEvent[];
	keyed: Map<string, DeferredEvent>;
}

interface BoardEventsContextType {
	emit: (event: BoardEvent, options?: EmissionOptions) => void;
	subscribe: <T extends BoardEvent["type"]>(
		type: T,
		listener: BoardEventListener<T>,
		options?: SubscriptionOptions,
	) => () => void;
	clearDeferredEvents: () => void;
}

const BoardEventsContext = createContext<BoardEventsContextType | null>(null);

BoardEventsContext.displayName = "BoardEventsContext";

// Additional emit function that can be used to emit events from outside the BoardEventsProvider
let globalEmit:
	| ((event: BoardEvent, options?: EmissionOptions) => void)
	| null = null;

// Export for testing purposes
export function resetGlobalEmitter() {
	globalEmit = null;
}

function isEventOfType<T extends BoardEvent["type"]>(
	type: T,
	event?: BoardEvent,
): event is Extract<BoardEvent, { type: T }> {
	if (!event) {
		return false;
	}

	return event.type === type;
}

export function BoardEventsProvider({ children }: { children: ReactNode }) {
	const listeners = useMemo<
		Map<BoardEvent["type"], Map<string | undefined, Set<BoardEventListener>>>
	>(() => new Map(), []);

	const deferredEvents = useRef<Map<BoardEvent["type"], DeferredEventsStorage>>(
		new Map(),
	);

	const emit = useCallback(
		(event: BoardEvent, options: EmissionOptions = {}) => {
			const { mode = "immediate", key } = options;

			switch (mode) {
				case "immediate": {
					const listenersForType = listeners.get(event.type);
					if (listenersForType) {
						if (key !== undefined) {
							// If a key is provided, only emit to listeners with that key
							const keyedListeners = listenersForType.get(key);
							keyedListeners?.forEach((listener) => listener(event));
						} else {
							// If no key is provided, emit to all unkeyed listeners
							const unkeyedListeners = listenersForType.get(undefined);
							unkeyedListeners?.forEach((listener) => listener(event));
						}
					}
					break;
				}
				default: {
					const storage = deferredEvents.current.get(event.type) || {
						unkeyed: [],
						keyed: new Map(),
					};

					const deferredEvent: DeferredEvent = {
						event,
						mode,
						key,
					};

					if (key) {
						storage.keyed.set(key, deferredEvent);
					} else {
						storage.unkeyed.push(deferredEvent);
					}

					deferredEvents.current.set(event.type, storage);
					break;
				}
			}
		},
		[listeners],
	);

	/**
	 * Set the global emit function
	 * This is used to emit events from outside the BoardEventsProvider
	 */
	useEffect(() => {
		globalEmit = emit;
		return () => {
			globalEmit = null;
		};
	}, [emit]);

	const subscribe = useCallback(
		<T extends BoardEvent["type"]>(
			type: T,
			listener: BoardEventListener<T>,
			options: SubscriptionOptions = {},
		) => {
			const { key } = options;
			const storage = deferredEvents.current.get(type);
			let consumedDeferredKey: string | null = null;

			// Handle deferred-once events
			if (storage) {
				if (key) {
					// Look for keyed deferred-once event
					const keyedEvent = storage.keyed.get(key);
					if (
						keyedEvent &&
						keyedEvent.mode === "deferred-once" &&
						isEventOfType(type, keyedEvent.event)
					) {
						listener(keyedEvent.event);
						storage.keyed.delete(key);
						return () => {};
					}
				} else {
					// Look for unkeyed deferred-once event
					const unkeyedOnceIndex = storage.unkeyed.findIndex(
						(event) => event.mode === "deferred-once",
					);
					if (unkeyedOnceIndex !== -1) {
						const unkeyedOnceEvent = storage.unkeyed[unkeyedOnceIndex];
						if (isEventOfType(type, unkeyedOnceEvent.event)) {
							listener(unkeyedOnceEvent.event);
							storage.unkeyed.splice(unkeyedOnceIndex, 1);
							return () => {};
						}
					}
				}

				// Handle regular deferred events
				if (key) {
					// Look for keyed deferred event
					const keyedEvent = storage.keyed.get(key);
					if (
						keyedEvent &&
						keyedEvent.mode === "deferred" &&
						isEventOfType(type, keyedEvent.event)
					) {
						listener(keyedEvent.event);
						consumedDeferredKey = key;
					}
				} else {
					// Look for unkeyed deferred event
					const unkeyedEvent = storage.unkeyed.find(
						(event) => event.mode === "deferred",
					);
					if (unkeyedEvent && isEventOfType(type, unkeyedEvent.event)) {
						listener(unkeyedEvent.event);
					}
				}
			}

			// Add listener to active listeners with key awareness
			let listenersForType = listeners.get(type);

			if (!listenersForType) {
				listenersForType = new Map();
				listeners.set(type, listenersForType);
			}

			let listenersForKey = listenersForType.get(key) as
				| Set<BoardEventListener<T>>
				| undefined;
			if (!listenersForKey) {
				listenersForKey = new Set([listener]);
				listenersForType.set(
					key,
					listenersForKey as unknown as Set<BoardEventListener>,
				);
			} else {
				listenersForKey.add(listener);
			}

			return () => {
				const currentListenersForType = listeners.get(type);
				const currentListenersForKey = currentListenersForType?.get(key) as
					| Set<BoardEventListener<T>>
					| undefined;
				currentListenersForKey?.delete(listener);

				// Clean up empty sets and maps
				if (currentListenersForKey?.size === 0) {
					currentListenersForType?.delete(key);
				}
				if (currentListenersForType?.size === 0) {
					listeners.delete(type);
				}

				// Clean up the specific keyed deferred event if we consumed one
				if (consumedDeferredKey && key) {
					const currentStorage = deferredEvents.current.get(type);
					currentStorage?.keyed.delete(consumedDeferredKey);
				}
			};
		},
		[listeners],
	);

	const clearDeferredEvents = useCallback(() => {
		deferredEvents.current = new Map();
	}, []);

	const value = useMemo(
		() => ({
			emit,
			subscribe,
			clearDeferredEvents,
		}),
		[emit, subscribe, clearDeferredEvents],
	);

	return (
		<BoardEventsContext.Provider value={value}>
			{children}
		</BoardEventsContext.Provider>
	);
}

export function useBoardEvents() {
	const context = useContext(BoardEventsContext);

	if (!context) {
		return {
			emit: () => {},
			subscribe: () => () => {},
			clearDeferredEvents: () => {},
		};
	}

	return context;
}

/**
 * Get the global emit function for the BoardEventsContext
 * to use in classes like BaseCardShapeUtils where hooks can't be used
 * @returns The global emit function
 */
export function getBoardEventsEmitter() {
	if (!globalEmit) {
		throw new Error("BoardEventsEmitter not initialized");
	}
	return globalEmit;
}
